import type { SnEvent } from './types'

/** Months either side of now that `/games/dates` is queried for. */
const WINDOW_MONTHS = 6

const addMonths = (date: Date, months: number): Date => {
  const shifted = new Date(date)
  shifted.setMonth(shifted.getMonth() + months)
  return shifted
}

/**
 * The `starts_at` values of events falling inside the same ±6 month window
 * `/games/dates` is queried for.
 *
 * Games are fetched for a bounded window; events arrive unwindowed. Without
 * this, an event years out could decide a tab that games never saw a candidate
 * for — so both sides of the tab decision are narrowed to the same question.
 *
 * Missing and unparseable dates are dropped rather than passed through as NaN.
 */
export const eventStartsWithinWindow = (
  events: SnEvent[] | null | undefined,
  now: Date = new Date()
): string[] => {
  const start = addMonths(now, -WINDOW_MONTHS).getTime()
  const end = addMonths(now, WINDOW_MONTHS).getTime()

  return (events ?? [])
    .map((event) => event?.starts_at)
    .filter((startsAt): startsAt is string => {
      if (!startsAt) {
        return false
      }
      const time = new Date(startsAt).getTime()
      return !Number.isNaN(time) && time >= start && time <= end
    })
}

/** `date` snapped to midnight UTC, so later arithmetic cannot cross a day. */
const utcMidnight = (date: Date): Date =>
  new Date(`${date.toISOString().slice(0, 10)}T00:00:00.000Z`)

/** Start of `date`'s UTC day, as an ISO instant. */
const startOfUtcDay = (date: Date): string =>
  `${date.toISOString().slice(0, 10)}T00:00:00.000Z`

/** End of `date`'s UTC day, as an ISO instant. */
const endOfUtcDay = (date: Date): string =>
  `${date.toISOString().slice(0, 10)}T23:59:59.999Z`

/**
 * The same ±`WINDOW_MONTHS` span as bounds, for the QUERY that fetches the
 * events `eventStartsWithinWindow` then narrows.
 *
 * Exported so the three opening-tab decision callers (web `GameList`, web
 * `MyScheduleList`, mobile `CombinedScheduleGamesContext`) cannot drift from
 * the constant above — a query wider than the filter wastes a fetch, and one
 * narrower silently drops events the decision needed.
 *
 * The window is sent EXPLICITLY rather than omitted: `/events/membership`
 * answers an unwindowed request with an empty list, which reads as "nothing
 * ahead" and decides every load onto PAST.
 *
 * **Quantized to the DAY, deliberately.** These bounds become part of a
 * react-query key, and callers compute them during render. A millisecond- or
 * second-precision `new Date()` therefore yields a NEW key on every render —
 * which refetches, which re-renders, which refetches: an endless request loop.
 * Day granularity makes the value stable for the whole day, so the key settles
 * after the first fetch. The extra ~1 day of span either side is harmless: the
 * result is filtered by `eventStartsWithinWindow` anyway, and the decision only
 * asks whether anything exists today or later.
 */
export const resolveDecisionEventWindow = (
  now: Date = new Date()
): { startsAfter: string; startsBefore: string; order: 'asc' } => ({
  // Rounded OUTWARD — start to the beginning of its day, end to the end of
  // its day — so the query is never narrower than the filter it feeds.
  //
  // `now` is snapped to UTC midnight BEFORE the month arithmetic, not after:
  // `addMonths` uses `setMonth`, which works in LOCAL time, so an instant late
  // in the UTC day lands on the next local day and shifts the whole window.
  // Snapping first is what makes the result identical for every instant within
  // a day — the stability the query key depends on.
  startsAfter: startOfUtcDay(addMonths(utcMidnight(now), -WINDOW_MONTHS)),
  startsBefore: endOfUtcDay(addMonths(utcMidnight(now), WINDOW_MONTHS)),
  order: 'asc',
})
